export type AppRole =
  | 'ADMIN'
  | 'GESTOR_ADMIN'
  | 'MASTER'
  | 'FINANCEIRO'
  | 'GESTOR'
  | 'FISCAL'
  | 'CREDENCIADO'
  | 'AUDITOR'
  | 'RESPONSAVEL'
  | 'OPERADOR_DRE'
  | 'GESTOR_DRE'
  | 'OPERADOR_SME'
  | 'GESTOR_SME'
  | 'ADMIN_CREDENCIADO'
  | 'OPERADOR_CREDENCIADO'
  | 'FINANCEIRO_CREDENCIADO'
  | 'MASTER_FINANCEIRO'
  | 'JURIDICO_DTEG';

export type PermissionAction = 'visualizar' | 'criar' | 'editar' | 'excluir';

export interface PermissionModule {
  key: string;
  label: string;
  actions: PermissionAction[];
}

export interface PermissionGroup {
  label: string;
  modules: PermissionModule[];
}

const ALL_ACTIONS: PermissionAction[] = ['visualizar', 'criar', 'editar', 'excluir'];
const VIEW_ONLY: PermissionAction[] = ['visualizar'];
const VIEW_EDIT: PermissionAction[] = ['visualizar', 'editar'];
const VIEW_CREATE_EDIT: PermissionAction[] = ['visualizar', 'criar', 'editar'];

export const PERMISSION_GROUPS: PermissionGroup[] = [
  {
    label: 'Principal',
    modules: [
      { key: 'dashboard', label: 'Dashboard', actions: VIEW_ONLY },
      { key: 'mapa', label: 'Mapa', actions: VIEW_ONLY },
    ],
  },
  {
    label: 'Cadastros',
    modules: [
      { key: 'credenciados', label: 'Credenciados', actions: ALL_ACTIONS },
      { key: 'condutores', label: 'Condutores', actions: ALL_ACTIONS },
      { key: 'monitores', label: 'Monitores', actions: ALL_ACTIONS },
      { key: 'veiculos', label: 'Veículos', actions: ALL_ACTIONS },
    ],
  },
  {
    label: 'Operacional',
    modules: [
      { key: 'termos', label: 'Termos', actions: ALL_ACTIONS },
      { key: 'credenciamento', label: 'Credenciamento', actions: VIEW_EDIT },
      { key: 'demandas', label: 'Demandas', actions: ALL_ACTIONS },
      { key: 'apuracao', label: 'Apuração Mensal', actions: VIEW_CREATE_EDIT },
      { key: 'ordens', label: 'Ordens de Serviço', actions: VIEW_CREATE_EDIT },
      { key: 'consultas', label: 'Consultas', actions: VIEW_ONLY },
      { key: 'importacao_eol', label: 'Importação EOL', actions: VIEW_ONLY },
      { key: 'fiscalizacao', label: 'Fiscalização', actions: VIEW_CREATE_EDIT },
      { key: 'publicacoes', label: 'Publicações', actions: VIEW_ONLY },
      { key: 'processos_sei', label: 'Processos SEI', actions: ALL_ACTIONS },
      { key: 'descredenciamento', label: 'Descredenciamento', actions: VIEW_CREATE_EDIT },
      { key: 'recursos', label: 'Recursos', actions: ALL_ACTIONS },
      { key: 'multas', label: 'Multas', actions: ALL_ACTIONS },
      { key: 'solicitacoes_servico', label: 'Solicitações Serviço', actions: VIEW_EDIT },
    ],
  },
  {
    label: 'Financeiro',
    modules: [
      { key: 'apontamentos', label: 'Apontamentos DRE', actions: VIEW_CREATE_EDIT },
      { key: 'aprovacao_sme', label: 'Aprovação SME', actions: VIEW_EDIT },
      { key: 'medicoes', label: 'Medições', actions: ALL_ACTIONS },
      { key: 'motor_calculo', label: 'Ateste de Medição', actions: VIEW_ONLY },
      { key: 'demonstrativos', label: 'Demonstrativos', actions: VIEW_ONLY },
      { key: 'bancada', label: 'Tabela de Preços Contratuais', actions: ALL_ACTIONS },
      { key: 'bancada_veiculos', label: 'Bancada Veículos', actions: VIEW_EDIT },
      { key: 'empenho_liquidacao', label: 'Empenho e Liquidação', actions: VIEW_CREATE_EDIT },
      { key: 'export_sof', label: 'Export SOF', actions: VIEW_ONLY },
      { key: 'acompanhamento_mensal', label: 'Acompanhamento Mensal', actions: VIEW_ONLY },
      { key: 'cobrancas', label: 'Cobranças DAMSP', actions: ALL_ACTIONS },
      {
        key: 'fechamento_bulk_matrix',
        label: 'Matriz de Ações em Lote (Fechamento)',
        actions: VIEW_EDIT,
      },
    ],
  },
  {
    label: 'Famílias',
    modules: [
      { key: 'solicitacoes_publicas', label: 'Solicitações Públicas', actions: VIEW_EDIT },
      { key: 'responsaveis', label: 'Responsáveis', actions: VIEW_ONLY },
    ],
  },
  {
    label: 'Relatórios',
    modules: [
      { key: 'relatorios', label: 'Relatórios', actions: VIEW_ONLY },
      { key: 'kpis', label: 'KPIs', actions: VIEW_ONLY },
    ],
  },
];

export const ACTION_LABELS: Record<PermissionAction, string> = {
  visualizar: 'Visualizar',
  criar: 'Criar',
  editar: 'Editar',
  excluir: 'Excluir',
};

export type PermissionsMap = Record<string, Partial<Record<PermissionAction, boolean>>>;

// Helper to build a full PermissionsMap with all modules enabled
function allPermissions(): PermissionsMap {
  const map: PermissionsMap = {};
  PERMISSION_GROUPS.flatMap((g) => g.modules).forEach((m) => {
    const p: Partial<Record<PermissionAction, boolean>> = {};
    m.actions.forEach((a) => (p[a] = true));
    map[m.key] = p;
  });
  return map;
}

// Helper to build permissions for specific module keys with specific actions
function buildPermissions(
  entries: Array<{ keys: string[]; actions: PermissionAction[] }>,
): PermissionsMap {
  const map: PermissionsMap = {};
  entries.forEach(({ keys, actions }) => {
    keys.forEach((key) => {
      const p: Partial<Record<PermissionAction, boolean>> = {};
      actions.forEach((a) => (p[a] = true));
      map[key] = p;
    });
  });
  return map;
}

/**
 * Static permission maps for each system role.
 * These reflect the actual RLS policies and ProtectedRoute rules.
 */
export const SYSTEM_ROLE_PERMISSIONS: Record<AppRole, PermissionsMap> = {
  ADMIN: allPermissions(),

  MASTER: allPermissions(),

  GESTOR_ADMIN: buildPermissions([
    {
      keys: [
        'dashboard',
        'mapa',
        'consultas',
        'importacao_eol',
        'publicacoes',
        'descredenciamento',
        'motor_calculo',
        'demonstrativos',
        'export_sof',
        'acompanhamento_mensal',
        'relatorios',
        'kpis',
        'responsaveis',
      ],
      actions: ['visualizar'],
    },
    { keys: ['solicitacoes_publicas'], actions: ['visualizar', 'editar'] },
    {
      keys: [
        'credenciados',
        'condutores',
        'monitores',
        'veiculos',
        'termos',
        'demandas',
        'processos_sei',
        'recursos',
      ],
      actions: ['visualizar', 'criar', 'editar', 'excluir'],
    },
    { keys: ['credenciamento'], actions: ['visualizar', 'editar'] },
    { keys: ['apuracao', 'ordens'], actions: ['visualizar', 'criar', 'editar'] },
    { keys: ['medicoes'], actions: ['visualizar', 'criar', 'editar'] },
    { keys: ['bancada'], actions: ['visualizar', 'criar', 'editar'] },
    { keys: ['bancada_veiculos'], actions: ['visualizar', 'editar'] },
    { keys: ['empenho_liquidacao'], actions: ['visualizar', 'criar', 'editar'] },
    { keys: ['cobrancas'], actions: ['visualizar'] },
  ]),

  GESTOR: buildPermissions([
    {
      keys: [
        'dashboard',
        'mapa',
        'consultas',
        'importacao_eol',
        'publicacoes',
        'descredenciamento',
        'relatorios',
        'kpis',
        'responsaveis',
      ],
      actions: ['visualizar'],
    },
    { keys: ['solicitacoes_publicas'], actions: ['visualizar', 'editar'] },
    {
      keys: [
        'credenciados',
        'condutores',
        'monitores',
        'veiculos',
        'termos',
        'demandas',
        'processos_sei',
        'recursos',
      ],
      actions: ['visualizar', 'criar', 'editar', 'excluir'],
    },
    { keys: ['credenciamento'], actions: ['visualizar', 'editar'] },
    { keys: ['apuracao', 'ordens'], actions: ['visualizar', 'criar', 'editar'] },
  ]),

  FINANCEIRO: buildPermissions([
    {
      keys: [
        'dashboard',
        'motor_calculo',
        'demonstrativos',
        'export_sof',
        'acompanhamento_mensal',
        'relatorios',
        'kpis',
        'termos',
      ],
      actions: ['visualizar'],
    },
    { keys: ['medicoes'], actions: ['visualizar', 'criar', 'editar', 'excluir'] },
    { keys: ['bancada'], actions: ['visualizar', 'criar', 'editar'] },
    { keys: ['bancada_veiculos'], actions: ['visualizar', 'editar'] },
    { keys: ['empenho_liquidacao'], actions: ['visualizar', 'criar', 'editar'] },
    { keys: ['cobrancas'], actions: ['visualizar', 'criar', 'editar', 'excluir'] },
    { keys: ['apuracao'], actions: ['visualizar', 'criar', 'editar'] },
  ]),

  FISCAL: buildPermissions([
    {
      keys: ['dashboard', 'mapa', 'consultas', 'publicacoes', 'relatorios', 'kpis'],
      actions: ['visualizar'],
    },
    { keys: ['credenciados', 'condutores', 'monitores', 'veiculos'], actions: ['visualizar'] },
    { keys: ['ordens'], actions: ['visualizar'] },
    { keys: ['fiscalizacao'], actions: ['visualizar', 'criar', 'editar'] },
    { keys: ['multas'], actions: ['visualizar', 'criar', 'editar', 'excluir'] },
  ]),

  CREDENCIADO: buildPermissions([{ keys: ['dashboard'], actions: ['visualizar'] }]),

  // Sub-perfis dentro do Credenciado (hierarquia organizacional)
  ADMIN_CREDENCIADO: buildPermissions([
    { keys: ['dashboard'], actions: ['visualizar'] },
    {
      keys: ['credenciados', 'condutores', 'monitores', 'veiculos'],
      actions: ['visualizar', 'criar', 'editar'],
    },
    { keys: ['solicitacoes_servico'], actions: ['visualizar', 'editar'] },
    { keys: ['medicoes', 'demonstrativos', 'cobrancas'], actions: ['visualizar'] },
  ]),

  OPERADOR_CREDENCIADO: buildPermissions([
    { keys: ['dashboard'], actions: ['visualizar'] },
    { keys: ['condutores', 'monitores', 'veiculos'], actions: ['visualizar', 'criar', 'editar'] },
    { keys: ['solicitacoes_servico'], actions: ['visualizar'] },
  ]),

  FINANCEIRO_CREDENCIADO: buildPermissions([
    { keys: ['dashboard', 'demonstrativos', 'cobrancas', 'medicoes'], actions: ['visualizar'] },
  ]),

  // Perfis DTEG ampliados
  MASTER_FINANCEIRO: buildPermissions([
    {
      keys: [
        'dashboard',
        'motor_calculo',
        'demonstrativos',
        'export_sof',
        'acompanhamento_mensal',
        'relatorios',
        'kpis',
        'termos',
      ],
      actions: ['visualizar'],
    },
    { keys: ['medicoes'], actions: ['visualizar', 'criar', 'editar', 'excluir'] },
    { keys: ['bancada', 'bancada_veiculos'], actions: ['visualizar', 'criar', 'editar'] },
    { keys: ['empenho_liquidacao'], actions: ['visualizar', 'criar', 'editar'] },
    { keys: ['cobrancas'], actions: ['visualizar', 'criar', 'editar', 'excluir'] },
    { keys: ['apuracao', 'apontamentos'], actions: ['visualizar', 'criar', 'editar'] },
    { keys: ['aprovacao_sme'], actions: ['visualizar', 'editar'] },
  ]),

  JURIDICO_DTEG: buildPermissions([
    {
      keys: [
        'dashboard',
        'credenciados',
        'condutores',
        'monitores',
        'veiculos',
        'ordens',
        'publicacoes',
        'processos_sei',
        'termos',
      ],
      actions: ['visualizar'],
    },
    { keys: ['multas', 'recursos'], actions: ['visualizar', 'criar', 'editar', 'excluir'] },
    { keys: ['fiscalizacao'], actions: ['visualizar', 'editar'] },
  ]),

  RESPONSAVEL: buildPermissions([{ keys: ['dashboard'], actions: ['visualizar'] }]),

  AUDITOR: buildPermissions([
    { keys: ['dashboard', 'relatorios', 'kpis'], actions: ['visualizar'] },
    {
      keys: ['credenciados', 'condutores', 'monitores', 'veiculos', 'termos', 'ordens'],
      actions: ['visualizar'],
    },
    { keys: ['fiscalizacao', 'multas'], actions: ['visualizar'] },
  ]),

  OPERADOR_DRE: buildPermissions([
    { keys: ['dashboard', 'ordens', 'credenciados'], actions: ['visualizar'] },
    { keys: ['apontamentos'], actions: ['visualizar', 'criar', 'editar'] },
  ]),

  GESTOR_DRE: buildPermissions([
    {
      keys: ['dashboard', 'ordens', 'credenciados', 'demonstrativos', 'relatorios', 'kpis'],
      actions: ['visualizar'],
    },
    { keys: ['apontamentos'], actions: ['visualizar', 'criar', 'editar'] },
    // Gestor DRE pode enviar lote para SME e abrir demanda DRE→SME
    { keys: ['aprovacao_sme'], actions: ['visualizar'] },
  ]),

  OPERADOR_SME: buildPermissions([
    {
      keys: [
        'dashboard',
        'mapa',
        'ordens',
        'credenciados',
        'demonstrativos',
        'relatorios',
        'kpis',
        'acompanhamento_mensal',
      ],
      actions: ['visualizar'],
    },
    { keys: ['apontamentos'], actions: ['visualizar'] },
    { keys: ['aprovacao_sme'], actions: ['visualizar', 'editar'] },
    { keys: ['medicoes'], actions: ['visualizar', 'editar'] },
  ]),

  GESTOR_SME: buildPermissions([
    {
      keys: [
        'dashboard',
        'mapa',
        'ordens',
        'credenciados',
        'demonstrativos',
        'relatorios',
        'kpis',
        'acompanhamento_mensal',
        'motor_calculo',
        'export_sof',
      ],
      actions: ['visualizar'],
    },
    { keys: ['apontamentos'], actions: ['visualizar'] },
    { keys: ['aprovacao_sme'], actions: ['visualizar', 'editar'] },
    { keys: ['medicoes'], actions: ['visualizar', 'editar'] },
  ]),
};

export const SYSTEM_ROLE_LABELS: Record<AppRole, { nome: string; descricao: string }> = {
  ADMIN: { nome: 'Administrador', descricao: 'Acesso total ao sistema' },
  MASTER: { nome: 'Master', descricao: 'Perfil técnico com acesso total ao sistema' },
  GESTOR_ADMIN: { nome: 'Gestor Administrativo', descricao: 'Gestão operacional e termos' },
  FINANCEIRO: { nome: 'Financeiro', descricao: 'Medições, cobranças e exportações' },
  GESTOR: { nome: 'Gestor', descricao: 'Gestão de cadastros e operações' },
  FISCAL: { nome: 'Fiscal', descricao: 'Fiscalização e multas' },
  CREDENCIADO: {
    nome: 'Credenciado (legado)',
    descricao: 'Acesso ao portal do credenciado — perfil legado, prefira ADMIN_CREDENCIADO',
  },
  ADMIN_CREDENCIADO: {
    nome: 'Admin do Credenciado',
    descricao: 'Representante legal — gerencia equipe e aprova solicitações dos colaboradores',
  },
  OPERADOR_CREDENCIADO: {
    nome: 'Operador do Credenciado',
    descricao: 'Colaborador operacional do credenciado (cadastros, OS)',
  },
  FINANCEIRO_CREDENCIADO: {
    nome: 'Financeiro do Credenciado',
    descricao: 'Visão financeira e medições do credenciado',
  },
  MASTER_FINANCEIRO: {
    nome: 'Master Financeiro DTEG',
    descricao: 'Financeiro DTEG com visão de todas as DREs',
  },
  JURIDICO_DTEG: {
    nome: 'Jurídico DTEG',
    descricao: 'Gestão de multas, recursos e processos administrativos',
  },
  RESPONSAVEL: { nome: 'Responsável', descricao: 'Área do responsável pelo aluno' },
  AUDITOR: { nome: 'Auditor', descricao: 'Auditoria e logs do sistema' },
  OPERADOR_DRE: { nome: 'Operador DRE', descricao: 'Digita apontamentos diários da própria DRE' },
  GESTOR_DRE: {
    nome: 'Gestor DRE',
    descricao: 'Chefia de uma DRE — envia lote para SME e abre demandas',
  },
  OPERADOR_SME: {
    nome: 'Operador SME',
    descricao: 'Aprova/devolve lotes de apontamentos das DREs',
  },
  GESTOR_SME: {
    nome: 'Gestor SME',
    descricao: 'Gestão completa do Portal SME e relatórios executivos',
  },
};
